/**
 * ArModal — AR Mode overlay using `<model-viewer>` for cross-platform AR.
 *
 * Platform behaviour:
 *   iOS    → Quick Look via USDZ (ar-modes="quick-look", ios-src)
 *   Android → WebXR     via GLB  (ar-modes="webxr",       src)
 *
 * Layout flow (clean transition):
 *   1. User taps AR button → modal opens with backdrop + CTA
 *   2. User taps "In AR erkunden" → model-viewer mounts (hidden via opacity:0)
 *      and engine is paused immediately (setArActive=true).
 *   3. 2 s later → activateAR() is called, WebXR session starts.
 *   4. ar-status "session-started" → model-viewer becomes visible (opacity:1),
 *      modal chrome hides.
 *   5. ar-status "session-ended" → auto-close modal, resume engine.
 *
 * model-viewer is loaded via <script> in index.html (NOT bundled by Vite)
 * to avoid Three.js deduplication conflicts with the R3F engine.
 *
 * @see docs/ims-engine-api/07-feature-ar.md
 */
import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { tracker, EVENTS } from '../../services/tracking';
import { useAppStore } from '../../store/app.store';
import type { ResolvedConfig } from '@ims/shared';
import { resolveArUrls, applyArMaterials, buildArMaterialChanges } from '@ims/shared';
import type { MvElement } from '@ims/shared';

// ---------------------------------------------------------------------------
// model-viewer JSX type augmentation (custom element → React JSX)
// ---------------------------------------------------------------------------
type ModelViewerElement = HTMLElement & {
  activateAR?: () => void;
  canActivateAR?: boolean;
  model?: unknown;
  loaded?: boolean;
};

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': React.DetailedHTMLProps<
        React.HTMLAttributes<ModelViewerElement> & {
          src?: string;
          'ios-src'?: string;
          ar?: boolean;
          'ar-modes'?: string;
          'environment-image'?: string;
          'ar-scale'?: string;
          'camera-controls'?: boolean;
          'shadow-intensity'?: string;
        },
        ModelViewerElement
      >;
    }
  }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const BTN_BG = '#657081';
const BTN_HOVER = '#2c343f';
const BTN_BORDER = '#657081';
const FADE_MS = 380;
const AR_DELAY = 2000; // ms to wait after mount before calling activateAR()
const ICON_AR = '/app/icons/icon-ar.svg';
const ICON_CLOSE = '/app/icons/icon-close.svg';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isIos(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * iOS Quick Look — programmatically trigger USDZ AR without model-viewer.
 *
 * Apple's Quick Look is a native system overlay; it requires only an
 * <a href="…usdz" rel="ar"> element to be clicked. No XR session, no
 * engine interaction needed — the browser just sits underneath untouched.
 */
function triggerIosQuickLook(usdzUrl: string): void {
  const anchor = document.createElement('a');
  anchor.href = usdzUrl;
  anchor.rel = 'ar';
  // Apple requires at least one child element (conventionally an <img>)
  anchor.appendChild(document.createElement('img'));
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

/**
 * Lazily injects model-viewer as a standalone <script> tag the first time
 * AR is requested. This keeps model-viewer's bundled Three.js completely
 * isolated from the R3F engine's Three.js until it is actually needed,
 * avoiding the "Multiple instances of Three.js" warning and engine crashes.
 */
let mvScriptPromise: Promise<void> | null = null;

function loadModelViewer(): Promise<void> {
  if (mvScriptPromise) return mvScriptPromise;
  // Already loaded (e.g. script tag added by a prior session)
  if (customElements.get('model-viewer')) {
    mvScriptPromise = Promise.resolve();
    return mvScriptPromise;
  }
  mvScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = '/app/vendor/model-viewer.min.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load model-viewer'));
    document.head.appendChild(script);
  });
  return mvScriptPromise;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface ArModalProps {
  open: boolean;
  onClose: () => void;
  config: ResolvedConfig | null;
}

export function ArModal({ open, onClose, config }: ArModalProps) {
  const [mounted, setMounted] = useState(false);
  const [animIn, setAnimIn] = useState(false);
  /** True after CTA click — model-viewer is mounted. */
  const [arLaunched, setArLaunched] = useState(false);
  /** True while the XR session is running — model-viewer takes over the screen. */
  const [arSessionActive, setArSessionActive] = useState(false);

  const rafRef = useRef<number | null>(null);
  const modelViewerRef = useRef<ModelViewerElement>(null);
  const arActivatedRef = useRef(false);

  const setArActive = useAppStore((s) => s.setArActive);
  const selectedColorId = useAppStore((s) => s.selectedColorId);
  const selectedPanoId = useAppStore((s) => s.selectedPanoId);

  // ── Resolve AR URLs from config + current user selection ───────────────
  const arConfig = config?.model.ar;
  const hdriUrl = arConfig?.androidHdriUrl ?? '/app/hdri/wooden_studio_17_1k.hdr';
  const isIOS = isIos();

  const steering = config?.market.steeringWheel ?? 'lhd';
  const effectiveColorId = selectedColorId ?? config?.colors[0]?.id ?? '';
  const effectiveInteriorId = selectedPanoId ?? config?.model.panoVariants?.[0]?.id;

  const { usdzUrl, glbUrl, needsColoring } = resolveArUrls(arConfig, {
    steering,
    colorId: effectiveColorId,
    interiorId: effectiveInteriorId,
    mainGlbUrl: config?.glbUrl ?? '',
  });

  // ── Look up full color definition + interior variant for coloring ──────
  const currentColor = config?.colors.find((c) => c.id === effectiveColorId);
  const currentInterior = config?.model.interiorVariants?.find((v) => v.id === effectiveInteriorId);

  // ── mount / unmount animation ──────────────────────────────────────────
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMounted(true);
      setArLaunched(false);
      setArSessionActive(false);
      arActivatedRef.current = false;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => setAnimIn(true));
      });
    } else {
      setAnimIn(false);
      const t = setTimeout(() => {
        setMounted(false);
        setArLaunched(false);
        setArSessionActive(false);
        arActivatedRef.current = false;
      }, FADE_MS);
      return () => clearTimeout(t);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [open]);

  // ── Wire model-viewer events (useEffect — runs after paint) ───────────
  useEffect(() => {
    const mv = modelViewerRef.current;
    if (!mv) return;

    const handleLoad = async () => {
      // In colored mode: apply materials then activate AR immediately.
      if (needsColoring && !arActivatedRef.current) {
        const changes = buildArMaterialChanges(currentColor, currentInterior);
        await applyArMaterials(mv as unknown as MvElement, changes);
        if (mv.canActivateAR) {
          arActivatedRef.current = true;
          mv.activateAR?.();
        }
      }
    };

    const handleArStatus = (event: Event) => {
      const status = (event as CustomEvent<{ status: string }>).detail.status;
      if (status === 'session-started') {
        setArSessionActive(true);
      } else if (status === 'session-ended' || status === 'not-presenting') {
        setArSessionActive(false);
        setArActive(false);
        onClose();
      }
    };

    mv.addEventListener('load', handleLoad);
    mv.addEventListener('ar-status', handleArStatus);

    return () => {
      mv.removeEventListener('load', handleLoad);
      mv.removeEventListener('ar-status', handleArStatus);
    };
  }, [arLaunched, setArActive, onClose, needsColoring, currentColor, currentInterior]);

  // ── Activate AR after delay (static mode only) ─────────────────────────
  useEffect(() => {
    if (!arLaunched || arActivatedRef.current || needsColoring) return;

    const t = setTimeout(() => {
      const mv = modelViewerRef.current;
      if (mv?.canActivateAR) {
        arActivatedRef.current = true;
        mv.activateAR?.();
      } else {
        console.warn('[ArModal] AR not available.');
      }
    }, AR_DELAY);

    return () => clearTimeout(t);
  }, [arLaunched, needsColoring]);

  // ── CTA click ─────────────────────────────────────────────────────────
  function handleLaunchClick() {
    tracker.track(EVENTS.AR_MODE);

    if (isIOS) {
      // iOS: Quick Look is a native system overlay — the browser stays
      // alive underneath it, so there is no need to pause the engine or
      // mount model-viewer.  Trigger the USDZ link directly and close the
      // modal so the user returns to a fully-working viewer.
      triggerIosQuickLook(usdzUrl);
      onClose();
      return;
    }

    // Android / desktop: full model-viewer + WebXR session flow.
    setArLaunched(true); // show "AR wird gestartet…" button state immediately
    setArActive(true); // pause engine + hide canvas
    loadModelViewer().catch((err) => {
      console.error('[ArModal] Failed to load model-viewer:', err);
    });
  }

  // ── Text ──────────────────────────────────────────────────────────────
  const text =
    config?.ui.arModalText ??
    'Der Augmented Reality (AR)-Modus ermöglicht es, das konfigurierte Fahrzeug in Ihrer realen Umgebung zu platzieren.';
  const buttonLabel = config?.ui.arModalButton ?? 'In AR erkunden';

  if (!mounted) return null;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      role="dialog"
      aria-modal="true"
      aria-label={buttonLabel}
    >
      {/* ── model-viewer ─────────────────────────────────────────────────
       *  Mounted when arLaunched=true. Hidden (opacity:0) until AR session
       *  starts — prevents the "model loading in corner" flash.
       *  position:relative + 100% sizing = how model-viewer expects layout.
       * ────────────────────────────────────────────────────────────────── */}
      {arLaunched && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: arSessionActive ? 200 : 101,
            opacity: arSessionActive ? 1 : 0,
            transition: 'opacity 200ms ease',
          }}
        >
          <model-viewer
            ref={modelViewerRef}
            src={glbUrl}
            ios-src={usdzUrl || undefined}
            ar
            camera-controls={!isIOS}
            ar-modes="webxr quick-look"
            environment-image={hdriUrl}
            shadow-intensity="2"
            style={{
              display: isIOS ? 'none' : 'block',
              width: isIOS ? '0' : '100%',
              height: isIOS ? '0' : '100%',
              position: isIOS ? ('absolute' as const) : ('relative' as const),
            }}
          />
        </div>
      )}

      {/* ── Modal chrome — hidden once AR session is active ──────────── */}
      {!arSessionActive && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backdropFilter: animIn ? 'blur(12px)' : 'blur(0px)',
              WebkitBackdropFilter: animIn ? 'blur(12px)' : 'blur(0px)',
              background: animIn ? 'rgba(0,0,0,0.58)' : 'rgba(0,0,0,0)',
              transition: [
                `backdrop-filter ${FADE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                `-webkit-backdrop-filter ${FADE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                `background ${FADE_MS}ms ease`,
              ].join(', '),
            }}
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Close button */}
          <div
            style={{
              position: 'absolute',
              top: 17.5,
              right: 20,
              zIndex: 1,
              opacity: animIn ? 1 : 0,
              transform: animIn ? 'translateY(0px)' : 'translateY(-6px)',
              transition: animIn
                ? 'opacity 280ms ease 180ms, transform 320ms ease 160ms'
                : 'opacity 160ms ease, transform 160ms ease',
            }}
          >
            <button
              type="button"
              aria-label="AR-Ansicht schließen"
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                padding: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.8,
              }}
            >
              <img src={ICON_CLOSE} alt="" style={{ width: 20, height: 20 }} aria-hidden="true" />
            </button>
          </div>

          {/* Content — text + CTA */}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 24,
              padding: '40px 32px',
              textAlign: 'center',
              opacity: animIn ? 1 : 0,
              transform: animIn ? 'scale(1) translateY(0px)' : 'scale(0.96) translateY(16px)',
              transition: animIn
                ? 'opacity 320ms ease 120ms, transform 480ms cubic-bezier(0.22, 1, 0.36, 1) 100ms'
                : 'opacity 200ms ease, transform 200ms ease',
            }}
          >
            <img
              src={ICON_AR}
              alt=""
              style={{ width: 64, height: 64, opacity: 0.9 }}
              aria-hidden="true"
            />

            <p
              style={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: 14,
                fontWeight: 300,
                lineHeight: 1.6,
                maxWidth: 288,
                margin: 0,
              }}
            >
              {text}
            </p>

            <ArButton onClick={handleLaunchClick} disabled={arLaunched}>
              {arLaunched ? 'AR wird gestartet …' : buttonLabel}
            </ArButton>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ArButton
// ---------------------------------------------------------------------------
function ArButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        marginTop: 8,
        width: 'auto',
        minWidth: 200,
        paddingLeft: 28,
        paddingRight: 28,
        height: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 50,
        border: `1px solid ${BTN_BORDER}`,
        backgroundColor: disabled ? '#444' : hovered ? BTN_HOVER : BTN_BG,
        color: '#fcfcfd',
        fontSize: 14,
        fontWeight: 400,
        letterSpacing: '0.04em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color 150ms ease, opacity 150ms ease',
      }}
    >
      {children}
    </button>
  );
}
